import api from './api';

export const trialService = {
  // =============================================================================
  // TRIAL MANAGEMENT
  // =============================================================================

  // Get all trials with pagination and filtering
  async getTrials(params = {}) {
    try {
      const response = await api.get('/trials', { params });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch trials');
    }
  },

  // Get trial by ID
  async getTrial(id) {
    try {
      const response = await api.get(`/trials/${id}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch trial');
    }
  },

  // Create new trial
  async createTrial(trialData) {
    try {
      const response = await api.post('/trials', trialData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to create trial');
    }
  },

  // Update trial
  async updateTrial(id, trialData) {
    try {
      const response = await api.put(`/trials/${id}`, trialData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to update trial');
    }
  },

  // Delete trial
  async deleteTrial(id) {
    try {
      const response = await api.delete(`/trials/${id}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to delete trial');
    }
  },

  // Bulk update trials
  async bulkUpdateTrials(updateData) {
    try {
      const response = await api.put('/trials/bulk', updateData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to bulk update trials');
    }
  },

  // =============================================================================
  // TRIAL ASSIGNMENTS
  // =============================================================================

  // Get trial assignments
  async getTrialAssignments(trialId) {
    try {
      const response = await api.get(`/trials/${trialId}/assignments`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch trial assignments');
    }
  },

  // Create trial assignment
  async createTrialAssignment(trialId, assignmentData) {
    try {
      const response = await api.post(`/trials/${trialId}/assignments`, assignmentData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to create trial assignment');
    }
  },

  // Update trial assignment
  async updateTrialAssignment(trialId, assignmentId, assignmentData) {
    try {
      const response = await api.put(`/trials/${trialId}/assignments/${assignmentId}`, assignmentData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to update trial assignment');
    }
  },

  // Delete trial assignment
  async deleteTrialAssignment(trialId, assignmentId) {
    try {
      const response = await api.delete(`/trials/${trialId}/assignments/${assignmentId}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to delete trial assignment');
    }
  },

  // =============================================================================
  // TRIAL SEARCH AND FILTERING
  // =============================================================================

  // Advanced trial search
  async searchTrials(searchCriteria) {
    try {
      const response = await api.post('/search/trials', searchCriteria);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to search trials');
    }
  },

  // Get trials by status
  async getTrialsByStatus(status) {
    try {
      const response = await api.get(`/trials?status=${status}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch trials by status');
    }
  },

  // Get trials by location
  async getTrialsByLocation(locationId, locationType = 'centre') {
    try {
      const response = await api.get(`/trials?${locationType}_id=${locationId}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch trials by location');
    }
  },

  // Get trials by crop
  async getTrialsByCrop(cropId) {
    try {
      const response = await api.get(`/trials?crop_id=${cropId}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch trials by crop');
    }
  },

  // Get trials by season
  async getTrialsBySeason(seasonId) {
    try {
      const response = await api.get(`/trials?season_id=${seasonId}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch trials by season');
    }
  },

  // =============================================================================
  // TRIAL EXPORT
  // =============================================================================

  // Export trial data to Excel
  async exportTrialData(trialId) {
    try {
      const response = await api.get(`/trials/${trialId}/export/excel`, {
        responseType: 'blob',
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to export trial data');
    }
  },

  // Export all trials to Excel
  async exportAllTrials(params = {}) {
    try {
      const response = await api.get('/trials/export/excel', {
        params,
        responseType: 'blob',
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to export trials');
    }
  },

  // =============================================================================
  // TRIAL STATISTICS
  // =============================================================================

  // Get trial statistics
  async getTrialStats() {
    try {
      const response = await api.get('/trials/stats');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch trial statistics');
    }
  },

  // Get trials by month
  async getTrialsByMonth(year) {
    try {
      const response = await api.get(`/trials/stats/by-month?year=${year}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch trials by month');
    }
  },

  // Get trials by status count
  async getTrialsByStatusCount() {
    try {
      const response = await api.get('/trials/stats/by-status');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch trials by status count');
    }
  },
};
